import { Controller } from '@hotwired/stimulus';

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface NavigatorWithStandalone extends Navigator {
    standalone?: boolean;
    getInstalledRelatedApps?: () => Promise<Array<{platform?: string; url?: string; id?: string}>>;
}

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
const installedStorageKey = 'ovasai:pwa-installed.v2';

const clearInstalledMarker = (): void => {
    try {
        window.localStorage.removeItem(installedStorageKey);
    } catch (error) {
        console.warn('installed app state cleanup failed', error);
    }
};

// Capture installability for the lifetime of the page, not only while an install
// component happens to be connected. This is essential during Turbo navigation:
// Chromium may fire the event before the user reaches the onboarding install step.
window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    // A fresh browser install prompt is strong evidence that the app is not
    // currently installed, so discard any stale durable marker.
    clearInstalledMarker();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    window.dispatchEvent(new CustomEvent('ovasai:pwa-install-available'));
});

window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    window.dispatchEvent(new CustomEvent('ovasai:pwa-installed'));
});

export class InstallPwaController extends Controller {
    declare readonly containerTarget: HTMLElement;
    declare readonly contentTarget: HTMLElement;
    declare readonly titleTarget: HTMLElement;
    declare readonly descriptionTarget: HTMLElement;
    declare readonly actionsTarget: HTMLElement;
    declare readonly buttonTarget: HTMLButtonElement;
    declare readonly statusTarget: HTMLElement;
    declare readonly instructionsTarget: HTMLElement;
    declare readonly floatingButtonTarget: HTMLButtonElement;
    declare readonly hasFloatingButtonTarget: boolean;
    declare readonly compactValue: boolean;

    static targets = ['container', 'content', 'title', 'description', 'actions', 'button', 'status', 'instructions', 'floatingButton'];
    static values = { compact: Boolean };

    private static readonly legacyInstalledStorageKey = 'ovasai:pwa-installed';
    private installPromptWait: number | null = null;
    private serviceWorkerReady = !('serviceWorker' in navigator);
    private installedThisSession = false;
    private readonly displayModeQueries = [
        window.matchMedia('(display-mode: standalone)'),
        window.matchMedia('(display-mode: fullscreen)'),
        window.matchMedia('(display-mode: minimal-ui)'),
    ];
    private readonly installAvailableHandler = () => {
        this.renderReadyState();
    };
    private readonly appInstalledHandler = () => {
        this.markInstalledForCurrentSession();
        this.renderInstalledState();
    };
    private readonly serviceWorkerReadyHandler = () => {
        this.serviceWorkerReady = true;
        if (!this.isInstalled() && !this.hasDeferredPrompt() && !this.isIos()) {
            this.renderInitialState();
        }
    };
    private readonly displayModeChangeHandler = () => {
        if (this.isInstalled()) {
            this.renderInstalledState();
            return;
        }

        if (this.hasDeferredPrompt()) {
            this.renderReadyState();
            return;
        }

        this.renderInitialState();
    };

    connect(): void {
        this.clearLegacyInstalledMarker();
        window.addEventListener('ovasai:pwa-install-available', this.installAvailableHandler);
        window.addEventListener('ovasai:pwa-installed', this.appInstalledHandler);
        window.addEventListener('ovasai:pwa-service-worker-ready', this.serviceWorkerReadyHandler);
        this.displayModeQueries.forEach((query) => {
            query.addEventListener('change', this.displayModeChangeHandler);
        });

        if (this.isInstalled()) {
            this.renderInstalledState();
            return;
        }

        if (this.hasDeferredPrompt()) {
            this.renderReadyState();
            return;
        }

        this.renderInitialState();
        this.refreshInstalledRelatedAppState();
        this.refreshServiceWorkerReadyState();
    }

    disconnect(): void {
        window.removeEventListener('ovasai:pwa-install-available', this.installAvailableHandler);
        window.removeEventListener('ovasai:pwa-installed', this.appInstalledHandler);
        window.removeEventListener('ovasai:pwa-service-worker-ready', this.serviceWorkerReadyHandler);
        this.displayModeQueries.forEach((query) => {
            query.removeEventListener('change', this.displayModeChangeHandler);
        });
        if (this.installPromptWait !== null) {
            window.clearTimeout(this.installPromptWait);
            this.installPromptWait = null;
        }
    }

    async install(): Promise<void> {
        if (this.isInstalled()) {
            this.renderInstalledState();
            return;
        }

        // Open a captured browser prompt immediately while this click still has
        // user activation. Awaiting service-worker work first can make the browser
        // reject an otherwise valid prompt.
        if (this.hasDeferredPrompt()) {
            await this.openDeferredPrompt();
            return;
        }

        if (!this.serviceWorkerReady && !this.isIos()) {
            this.setButtonState(true, 'Preparing install...', true);
            this.statusTarget.textContent = 'Checking whether this browser can install Ovasai...';
            this.showStatus();
            await this.waitForServiceWorkerReady();
            this.setButtonState(false, this.getButtonLabel());

            // A prompt captured during the preparation can only be opened by a
            // fresh user gesture. If the browser says the original gesture is
            // still active, use it now; otherwise make the next tap unmistakable.
            if (this.hasDeferredPrompt()) {
                if (window.navigator.userActivation?.isActive) {
                    await this.openDeferredPrompt();
                    return;
                }
                this.setButtonState(false, 'Install to home screen');
                this.statusTarget.textContent = 'Ovasai is ready. Tap Install to open your browser’s confirmation.';
                this.showStatus();
                return;
            }
        }

        this.renderManualInstructions();
    }

    private async openDeferredPrompt(): Promise<void> {
        const prompt = deferredInstallPrompt;
        if (!prompt) {
            this.renderManualInstructions();
            return;
        }

        deferredInstallPrompt = null;
        this.setButtonState(true);
        this.hideFloatingButton();
        this.statusTarget.textContent = 'Opening your install prompt...';
        this.showStatus();

        try {
            await prompt.prompt();
            const choice = await prompt.userChoice;

            if (choice.outcome === 'accepted') {
                this.statusTarget.textContent = 'Installing Ovasai on this device...';
                this.installPromptWait = window.setTimeout(() => {
                    if (!this.isInstalled()) {
                        this.statusTarget.textContent = 'If the app is now on your home screen, open it from there to finish.';
                        this.setButtonState(false, 'Installed? Open from home screen', false);
                    }
                }, 2500);
                return;
            }

            this.statusTarget.textContent = 'Installation cancelled. You can try again at any time.';
            this.renderInitialState();
            this.setButtonState(false);
        } catch (error) {
            console.warn('PWA install prompt failed', error);
            this.renderManualInstructions();
        }
    }

    private renderInitialState(): void {
        this.resetExpandedLayout();
        this.setButtonState(false, this.getButtonLabel());
        this.statusTarget.textContent = this.getDefaultStatus();
        this.instructionsTarget.classList.add('hidden');
        this.renderFloatingButtonAvailability();
        this.showStatus();
    }

    private renderReadyState(): void {
        this.resetExpandedLayout();
        this.setButtonState(false, 'Install to home screen');
        this.statusTarget.textContent = 'Your browser is ready to install Ovasai.';
        this.instructionsTarget.classList.add('hidden');
        this.showFloatingButton();
        this.showStatus();
    }

    private renderInstalledState(): void {
        this.markInstalledForCurrentSession();
        this.hideFloatingButton();
        this.containerTarget.className = this.compactValue ? 'pt-1' : 'border-t border-zinc-200/30 pt-6';
        this.contentTarget.className = 'flex items-center justify-between gap-3 px-4 py-3';
        this.titleTarget.className = 'text-sm font-medium text-zinc-600';
        this.descriptionTarget.classList.add('hidden');
        this.actionsTarget.className = 'flex items-center gap-2';
        this.buttonTarget.className = 'inline-flex items-center justify-center gap-2 rounded-full bg-green-50 px-3 py-2 text-xs font-semibold text-green-700';
        this.setButtonState(true, 'App is already installed!', true, 'fa-check');
        this.statusTarget.className = 'text-xs text-zinc-500';
        this.statusTarget.textContent = 'Open Ovasai from your home screen or app launcher.';
        this.instructionsTarget.classList.add('hidden');
        this.showStatus();
    }

    private renderManualInstructions(): void {
        this.resetExpandedLayout();
        this.setButtonState(false, this.getButtonLabel());
        this.statusTarget.textContent = this.getManualInstructionsIntro();
        this.instructionsTarget.innerHTML = this.getManualInstructionsList();
        this.instructionsTarget.classList.remove('hidden');
        this.renderFloatingButtonAvailability();
        this.containerTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.showStatus();
    }

    private getButtonLabel(): string {
        if (this.isIos()) {
            return 'Show install steps';
        }

        return this.serviceWorkerReady ? 'Install to home screen' : 'Prepare install';
    }

    private getDefaultStatus(): string {
        if (this.isIos()) {
            if (!this.isIosSafari()) {
                return 'Open Ovasai in Safari, then use Add to Home Screen.';
            }
            return 'Install on iPhone or iPad using Add to Home Screen.';
        }

        return 'Add Ovasai to your home screen for faster access and a full-screen app experience.';
    }

    private getManualInstructionsIntro(): string {
        if (this.isIos()) {
            return 'Follow these steps to add Ovasai to your home screen.';
        }

        if (this.isAndroid()) {
            return 'If your browser does not show the install prompt, use the browser menu instead.';
        }

        return 'Use your browser menu to install this app or add it to your home screen.';
    }

    private getManualInstructionsList(): string {
        if (this.isIos()) {
            const openSafariStep = this.isIosSafari()
                ? ''
                : '<li>1. Open this page in Safari.</li>';
            const stepOffset = this.isIosSafari() ? 0 : 1;
            return `
                <ol class="space-y-1">
                    ${openSafariStep}
                    <li>${stepOffset + 1}. Open the Share menu in Safari.</li>
                    <li>${stepOffset + 2}. Scroll and tap Add to Home Screen.</li>
                    <li>${stepOffset + 3}. Tap Add to finish installing Ovasai.</li>
                </ol>
                <p class="mt-2 text-xs text-zinc-500">Ovasai cannot add itself automatically; iOS requires this manual confirmation.</p>
            `;
        }

        if (this.isAndroid()) {
            return `
                <ol class="space-y-1">
                    <li>1. Open your browser menu.</li>
                    <li>2. Tap Install app or Add to Home screen.</li>
                    <li>3. Confirm to place Ovasai on your device.</li>
                </ol>
                <p class="mt-2 text-xs text-zinc-500">If that menu item is missing, wait a few seconds on this page, then open the menu again.</p>
            `;
        }

        return `
            <ol class="space-y-1">
                <li>1. Open your browser menu.</li>
                <li>2. Look for Install app or Add to Home screen.</li>
                <li>3. Confirm the install if your browser supports it.</li>
            </ol>
            <p class="mt-2 text-xs text-zinc-500">If that menu item is missing, your current browser may not support installing this app.</p>
        `;
    }

    private isInstalled(): boolean {
        return (
            this.installedThisSession ||
            this.hasInstalledMarker() ||
            this.displayModeQueries.some((query) => query.matches) ||
            (window.navigator as NavigatorWithStandalone).standalone === true ||
            document.referrer.startsWith('android-app://')
        );
    }

    private markInstalledForCurrentSession(): void {
        this.installedThisSession = true;
        try {
            window.localStorage.setItem(installedStorageKey, 'true');
        } catch (error) {
            console.warn('installed app state could not be saved', error);
        }
    }

    private hasInstalledMarker(): boolean {
        try {
            return window.localStorage.getItem(installedStorageKey) === 'true';
        } catch (error) {
            console.warn('installed app state could not be read', error);
            return false;
        }
    }

    private clearLegacyInstalledMarker(): void {
        try {
            window.localStorage.removeItem(InstallPwaController.legacyInstalledStorageKey);
        } catch (error) {
            console.warn('legacy installed state cleanup failed', error);
        }
    }

    private hasDeferredPrompt(): boolean {
        return deferredInstallPrompt !== null;
    }

    private async refreshInstalledRelatedAppState(): Promise<void> {
        const navigatorWithRelatedApps = window.navigator as NavigatorWithStandalone;
        if (!navigatorWithRelatedApps.getInstalledRelatedApps) {
            return;
        }

        try {
            const relatedApps = await navigatorWithRelatedApps.getInstalledRelatedApps();
            if (relatedApps.length > 0) {
                this.renderInstalledState();
            }
        } catch (error) {
            console.warn('installed app check failed', error);
        }
    }

    private waitForServiceWorkerReady(): Promise<void> {
        if (!('serviceWorker' in navigator)) {
            return Promise.resolve();
        }

        return Promise.race([
            navigator.serviceWorker.ready.then(() => {
                this.serviceWorkerReady = true;
            }),
            new Promise<void>((resolve) => {
                window.setTimeout(resolve, 1500);
            }),
        ]);
    }

    private refreshServiceWorkerReadyState(): void {
        if (!('serviceWorker' in navigator)) {
            this.serviceWorkerReady = true;
            return;
        }

        navigator.serviceWorker.ready.then(() => {
            this.serviceWorkerReady = true;
            if (this.hasDeferredPrompt()) {
                this.renderReadyState();
            } else if (!this.isInstalled() && !this.isIos()) {
                this.renderInitialState();
            }
        }).catch((error: unknown) => {
            console.warn('service worker readiness check failed', error);
        });
    }

    private isIos(): boolean {
        return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    }

    private isIosSafari(): boolean {
        const userAgent = window.navigator.userAgent;
        return this.isIos() && /safari/i.test(userAgent) && !/crios|fxios|edgios|opios/i.test(userAgent);
    }

    private isAndroid(): boolean {
        return /android/i.test(window.navigator.userAgent);
    }

    private showStatus(): void {
        this.statusTarget.classList.remove('hidden');
    }

    private setButtonState(
        disabled: boolean,
        label = this.getButtonLabel(),
        showIcon = true,
        iconClass = 'fa-download',
    ): void {
        this.buttonTarget.disabled = disabled;
        this.buttonTarget.innerHTML = showIcon
            ? `<i class="fas ${iconClass} text-sm" aria-hidden="true"></i><span>${label}</span>`
            : `<span>${label}</span>`;
    }

    private resetExpandedLayout(): void {
        this.containerTarget.className = this.compactValue ? 'pt-1' : 'border-t border-zinc-200/70 pt-8';
        this.contentTarget.className = 'flex flex-col gap-4';
        this.titleTarget.className = 'text-center text-base font-bold text-zinc-950';
        this.descriptionTarget.className = 'mx-auto mt-2 max-w-xl text-center text-sm/6 text-zinc-600';
        this.actionsTarget.className = 'flex flex-col items-center gap-3';
        this.buttonTarget.className = 'inline-flex w-full items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-green-500 disabled:cursor-not-allowed disabled:bg-zinc-400 sm:w-auto';
        this.statusTarget.className = 'hidden max-w-md text-center text-sm text-zinc-500';
    }

    private showFloatingButton(): void {
        if (!this.hasFloatingButtonTarget) {
            return;
        }

        this.floatingButtonTarget.disabled = false;
        this.floatingButtonTarget.classList.add('inline-flex');
        this.floatingButtonTarget.classList.remove('hidden');
    }

    private renderFloatingButtonAvailability(): void {
        if (this.isIos() || this.hasDeferredPrompt()) {
            this.showFloatingButton();
            return;
        }

        this.hideFloatingButton();
    }

    private hideFloatingButton(): void {
        if (!this.hasFloatingButtonTarget) {
            return;
        }

        this.floatingButtonTarget.disabled = true;
        this.floatingButtonTarget.classList.remove('inline-flex');
        this.floatingButtonTarget.classList.add('hidden');
    }
}
